import React from "react";
import { Router, Redirect, RouteComponentProps } from "@reach/router";
import { ApolloProvider } from "@apollo/client";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { ThemeProvider } from "@material-ui/core/styles";
import { PostHogProvider } from "posthog-js/react";
import { SnackbarProvider } from "components/haven-design/snackbar/context";
import ViewerProvider from "components/viewer/provider";

import theme from "components/theme";
import { TimeoutLogoutComponent } from "components/TimeoutLogoutComponent";

import { LoginPage } from "components/login";
import { TablesRefProvider } from "components/table-refs";
import { apolloClient } from "data/graphql";

import { EmptyLayout, MainLayout } from "pages/Layouts";

import NotFound from "pages/not-found";
import NotAuthorized from "pages/not-authorized";

import Home from "pages/home/home";
import About from "pages/static/about";
import Support from "pages/support/support";
import Demo from "pages/demo";
import DemoThanks from "pages/demo/demo-thanks";
import Terms from "pages/static/terms";

import JobsPage from "pages/jobs";
import FullStackEng from "pages/jobs/engineering-full-stack";
import AccountExec from "pages/jobs/account-exec";
import ProductManager from "pages/jobs/product-manager";
import ProductDesigner from "pages/jobs/product-designer";
import HeadOfSales from "pages/jobs/head-sales";

import LinkApp from "pages/link-app";

import UpdateForm from "pages/waitlist-update/update-form/components";

import Logout from "pages/logout";
import ResetPassword from "pages/login/reset-password";
import CheckEmail from "pages/login/check-email";
import ChoosePassword from "pages/login/choose-password";
import { NewUser } from "pages/login/new-user";
import { NewUserUpdate } from "pages/login/new-user-update";
import NoAccount from "pages/login/no-account";
import EmailVerified from "pages/login/verified";

import EligibilityLayoutV2 from "pages/eligibility/layout";
import EligibilityFormV2 from "pages/eligibility";
import FormCompleted from "pages/eligibility/components/form-completed";

import FormLayout from "pages/form";

import ClientDashboard from "pages/clients";
import AdminDashboard from "pages/dashboard/admin";
import ManagerDashboard from "pages/dashboard/manager";

import Properties from "pages/properties";


const ScrollToTopOfPage: React.FC<RouteComponentProps> = (props) => {
  const interestsListRegex = /^\/dash\/manager\/lists\/[\w-]+\/p\//;
  const interestSlideoutRegex = /^\/dash\/manager\/lists\/[\w-]+\/p\/\d\/interests\/[\w-]+/;

  React.useEffect(() => {
    if (window.location.pathname.match(interestSlideoutRegex)) return;
    if (window.location.pathname.match(interestsListRegex)) return;

    window.scrollTo(0, 0);
  }, [props.location?.pathname]);

  return <>{props.children}</>;
};

const emptyLayout = () => (
  <>
    <EmptyLayout path="/check-email">
      <CheckEmail path="/" />
    </EmptyLayout>
    <EmptyLayout path="/choose-password">
      <ChoosePassword path="/:token" />
    </EmptyLayout>
    <EmptyLayout path="/new-user">
      <NewUserUpdate path="/update" />
      <NewUser path="/:token" />
    </EmptyLayout>
    <EmptyLayout path="/no-account">
      <NoAccount path="/" />
    </EmptyLayout>
    <EmptyLayout path="/email-verified">
      <EmailVerified path="/" />
    </EmptyLayout>

    <Logout path="/logout" />
  </>
);

const mainLayout = () => (
  <>
    <MainLayout path="/" showHeader showFooter backgroundColor="white">
      <Properties path="/properties" />
      <About path="/about" />
      <Terms path="/terms" />
      <Support path="support/*" />
      <Demo path="demo" />
      <DemoThanks path="demo-thanks" />
      <NotAuthorized path="not-authorized" />
      <NotFound default />
      <LoginPage path="/login" />
      <LoginPage path="/signup" isSignup />
      <ResetPassword path="/reset-password" />
    </MainLayout>

    <MainLayout path="/careers" showHeader showFooter backgroundColor="white">
      <JobsPage path="/" />
      <FullStackEng path="full-stack-engineer" />
      <AccountExec path="account-exec" />
      <ProductManager path="product-manager" />
      <ProductDesigner path="product-designer" />
      <HeadOfSales path="head-sales" />
      <NotFound default />
    </MainLayout>

    <LinkApp path="/link-app/*" />
  </>
);

const postHogOptions = {
  // eslint-disable-next-line camelcase
  api_host: process.env.PUBLIC_POSTHOG_HOST,
};

const App: React.FC = () => (
  <PostHogProvider options={postHogOptions}
    apiKey={process.env.PUBLIC_POSTHOG_KEY ?? "" }>
    <ThemeProvider theme={theme}>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <ApolloProvider client={apolloClient}>
          <ViewerProvider>
            <SnackbarProvider>
              <TablesRefProvider>
                <TimeoutLogoutComponent />
                <Router>
                  <ScrollToTopOfPage path="/">
                    <ClientDashboard path="/dash/client/*" />

                    <AdminDashboard path="/dash/admin/*" />

                    <ManagerDashboard path="/dash/manager/*" />

                    <UpdateForm path="/update/:code" />

                    <EligibilityLayoutV2 path="/application">
                      <EligibilityFormV2 path="/:submissionId/*" />
                    </EligibilityLayoutV2>

                    <FormCompleted path="/form-completed" />

                    <FormLayout path="/form/:formId" />
                    <FormLayout path="/signup/:formId" />

                    <Home path="/" />
                    {mainLayout()}
                    {emptyLayout()}
                    <Redirect from="/login-mobile" to="/login" noThrow />
                  </ScrollToTopOfPage>
                </Router>
              </TablesRefProvider>
            </SnackbarProvider>
          </ViewerProvider>
        </ApolloProvider>
      </MuiPickersUtilsProvider>
    </ThemeProvider>
  </PostHogProvider>
);

export default App;
